import React, { useEffect, useState } from "react";
import { Routes, Route, useNavigate, useLocation } from "react-router-dom";
import { connect, useDispatch } from "react-redux";
import {
  MsalProvider,
  AuthenticatedTemplate,
  useMsal,
  UnauthenticatedTemplate,
} from "@azure/msal-react";
import { loginRequest } from "./AuthConfig";
import { PageLayout } from "./PageLayout";
import Login from "./common/Login";
import { ThemeProvider } from "@mui/material";
import theme from "./theme";
import "./index.css";
import UserDetailsServices from "./services/console_services/LoginLogot";
import { setActiveRoute, TabNameChange } from "./store/Actions";
import routes from "./setup/routes-manager/Routes"; // Import routes
import ErrorPage from "./pages/ErrorPage";
import { getAccessibleRoutes } from "./common/hooks/useRoleBasedAccess";
import PowerBi_Viewer from "./pages/PowerBi_Viewer";
import PageTracker from "./pageTracker";

const App = ({
  instance,
  networkFilterChange,
  DCFilterChange,
  productsFilterChange,
  customersFilterChange,
  dateRangeFilterChange,
  ApplyFilterActions,
  cl6FilterChange,
  pl3FilterChange,
  divisionFilterChange,
  _Network,
  tabChange,
}) => {
  const navigate = useNavigate();
  // const location = useLocation();
  const dispatch = useDispatch();
  const [userDetails, setUserDetails] = useState(false);

  const fetchAndSaveUserDetails = () => {
    UserDetailsServices.getUserDetails(instance)
      .then((data) => {
        if (data) {
          localStorage.setItem("userDetails", JSON.stringify(data));
          if (data?.AccessDetails?.length === 0) {
            navigate("/error");
          }
        }
      })
      .catch((error) => console.error("Error fetching user details:", error));
  };

  const setTabBasedOnPath = (path) => {
    if (path === "/if") {
      dispatch(TabNameChange("IF"));
      tabChange(0);
    } else if (path === "/sno") {
      dispatch(TabNameChange("SNO"));
      tabChange(0);
    } else if (path === "/dom") {
      dispatch(TabNameChange("DOM"));
      tabChange(1);
    } else if (path === "/rr") {
      dispatch(TabNameChange("RR"));
      tabChange(2);
    } else if (path === "/dp") {
      dispatch(TabNameChange("DP"));
      tabChange(0);
    } else if (path === "/do") {
      dispatch(TabNameChange("DO"));
      tabChange(1);
    } else if (path === "/dip") {
      dispatch(TabNameChange("DIP"));
      tabChange(0);
    } else if (path === "/cp") {
      dispatch(TabNameChange("CP"));
      tabChange(1);
    } else if (path === "/nps") {
      dispatch(TabNameChange("NPS"));
      tabChange(2);
    } else if (path === "/fgs") {
      dispatch(TabNameChange("FGS"));
      tabChange(0);
    } else if (path === "/poos") {
      dispatch(TabNameChange("POOS"));
      tabChange(1);
    } else if (path === "/slp") {
      dispatch(TabNameChange("SLP"));
      tabChange(0);
    } else if (path === "/iof") {
      dispatch(TabNameChange("IOF"));
      tabChange(1);
    } else if (path === "/iofpb") {
      dispatch(TabNameChange("IOFPB"));
      tabChange(2);
    }
    // else if (path === "/ff") {
    //   dispatch(TabNameChange("FF"));
    //   tabChange(0);
    // }
    else if (path === "/ds") {
      dispatch(TabNameChange("DETENTION"));
      tabChange(0);
    } else if (path === "/tm") {
      dispatch(TabNameChange("TM"));
      tabChange(2);
    }
  };

  useEffect(() => {
    if (userDetails) {
      fetchAndSaveUserDetails();
    }
  }, []);
  // Use the custom hook to get accessible routes.
  const accessibleRoutes = getAccessibleRoutes(routes);

  useEffect(() => {
    const handleLogin = async () => {
      try {
        await instance.initialize();
        const response = await instance.handleRedirectPromise();

        if (response) {
          handleSuccessfulLogin(response);
        }
      } catch (error) {
        console.error("Error during login:", error);
      }
    };

    const handleSuccessfulLogin = (response) => {
      UserDetailsServices.getUserDetails(instance).then((data) => {
        if (data) {
          setUserDetails(true);
          localStorage.setItem("userDetails", JSON.stringify(data));
          const targetFromLanding = sessionStorage.getItem("internalAppTarget");
          if (targetFromLanding) {
            setTabBasedOnPath(targetFromLanding);
            navigate(targetFromLanding);
            sessionStorage.removeItem("internalAppTarget");
          } else if (data?.loginLogOutDetails[0]?.LastViewPage) {
            setTabBasedOnPath(data?.loginLogOutDetails[0]?.LastViewPage);
            navigate(data?.loginLogOutDetails[0]?.LastViewPage);
          }
          const filterMapping = {
            Network: networkFilterChange,
            RecommendedDC: DCFilterChange,
            Products: productsFilterChange,
            Customers: customersFilterChange,
            PL3: pl3FilterChange,
            CL6: cl6FilterChange,
            Division: divisionFilterChange,
            Date: dateRangeFilterChange,
          };
          const storedUserDetails = localStorage.getItem("userDetails");
          const userDetails = storedUserDetails
            ? JSON.parse(storedUserDetails)
            : null;
          const loginFilter = userDetails?.loginLogOutDetails[0]?.LastFilterID;

          if (
            loginFilter &&
            typeof loginFilter === "object" &&
            Object.keys(loginFilter).length !== 0
          ) {
            Object.entries(filterMapping).forEach(([key, updateFunction]) => {
              if (loginFilter[key]) {
                updateFunction(loginFilter[key]);
              }
            });
            const filterValues = [
              {
                Network:
                  userDetails?.loginLogOutDetails[0]?.LastFilterID?.Network,
                DC: userDetails?.loginLogOutDetails[0]?.LastFilterID?.DC,
                Products:
                  userDetails?.loginLogOutDetails[0]?.LastFilterID?.Products,
                Customers:
                  userDetails?.loginLogOutDetails[0]?.LastFilterID?.Customers,
                PL3: userDetails?.loginLogOutDetails[0]?.LastFilterID?.PL3,
                CL6: userDetails?.loginLogOutDetails[0]?.LastFilterID?.CL6,
                Date: userDetails?.loginLogOutDetails[0]?.LastFilterID?.date,
                Division:
                  userDetails?.loginLogOutDetails[0]?.LastFilterID?.Division,
              },
            ];
            ApplyFilterActions(filterValues);
          }
          if (data?.AccessDetails?.length === 0) {
            navigate("/error");
          }
        }
      });
      saveAccessToken(response.accessToken);
      updateLastLoginTime();
    };
    const saveAccessToken = (token) => {
      localStorage.setItem("token", token);
    };
    const updateLastLoginTime = () => {
      const currentTime = new Date().toISOString();
      const params = { LoginTime: currentTime };
      UserDetailsServices.updateLastView(params, instance);
    };
    handleLogin();
  }, [instance]);

  const handleLoginRedirect = async () => {
    try {
      await instance.initialize();
      await instance.loginRedirect(loginRequest);
    } catch (error) {
      console.error("handleLoginRedirect", error);
    }
  };

  useEffect(() => {
    setTabBasedOnPath(window.location.pathname);
  }, []);

  const MainContent = () => {
    const { accounts } = useMsal();
    const activeAccount = accounts[0];

    return (
      <div className="App">
        <AuthenticatedTemplate>
          <PageTracker user={accounts} /> 
          <Routes>
            {activeAccount && (
              <>
                {accessibleRoutes.map((route, index) => (
                  <Route
                    key={index}
                    path={route.path}
                    element={route.component}
                  />
                ))}
              </>
            )}
            <Route path="/error" element={<ErrorPage />} />
            <Route
              path="/powerbi/:group?/:report?"
              element={<PowerBi_Viewer />}
            ></Route>
          </Routes>
        </AuthenticatedTemplate>
        <UnauthenticatedTemplate>
          <Login handleLoginRedirect={handleLoginRedirect} />
        </UnauthenticatedTemplate>
      </div>
    );
  };

  return (
    <MsalProvider instance={instance}>
      <ThemeProvider theme={theme}>
        <PageLayout>
          <MainContent />
        </PageLayout>
      </ThemeProvider>
    </MsalProvider>
  );
};

const mapStateToProps = (state) => ({
  Network: state.filter.Network,
  DC: state.filter.DC,
  Products: state.filter.Products,
  Customers: state.filter.Customers,
  PL3: state.filter.PL3,
  CL6: state.filter.CL6,
  date: state.filter.Date,
  Division: state.filter.Division,
});

const mapDispatchToProps = (dispatch) => ({
  networkFilterChange: (newValue) =>
    dispatch({ type: "FILTER_NETWORK", payload: newValue }),
  DCFilterChange: (newValue) =>
    dispatch({ type: "FILTER_DC", payload: newValue }),
  productsFilterChange: (newValue) =>
    dispatch({ type: "FILTER_PRODUCTS", payload: newValue }),
  customersFilterChange: (newValue) =>
    dispatch({ type: "FILTER_CUSTOMERS", payload: newValue }),
  pl3FilterChange: (newValue) =>
    dispatch({ type: "FILTER_PL3", payload: newValue }),
  cl6FilterChange: (newValue) =>
    dispatch({ type: "FILTER_CL6", payload: newValue }),
  divisionFilterChange: (newValue) =>
    dispatch({ type: "FILTER_DIVISION", payload: newValue }),
  dateRangeFilterChange: (newValue) =>
    dispatch({ type: "FILTER_DATERANGE", payload: newValue }),
  ApplyFilterActions: (newValue) =>
    dispatch({ type: "APPLY_FILTER", payload: newValue }),
  tabChange: (newValue) => dispatch({ type: "TAB_INDEX", payload: newValue }),
});

export default connect(mapStateToProps, mapDispatchToProps)(App);
